import examples from "libphonenumber-js/examples.mobile.json";
import {
  type CountryCode,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

/**
 * Countries offered in the selector, in the order shown. Deliberately a short
 * curated list rather than all ~240 ISO countries: these cover effectively all
 * real traffic for NL body-shop dossiers. Adding one here is all that's needed
 * to support it — validation and formatting come from libphonenumber's full
 * metadata regardless of this list.
 */
const SUPPORTED_COUNTRIES: readonly CountryCode[] = ["NL", "BE", "DE"];

/**
 * Default country for a new dossier. Admin work here is NL-first, so the
 * phone field starts there and the selector is only touched for the minority
 * of foreign drivers.
 */
export const DEFAULT_PHONE_COUNTRY: CountryCode = "NL";

/**
 * Dutch display names for ISO country codes, via the platform's own Intl
 * data — no hand-maintained country-name table to drift out of date.
 * `Intl.DisplayNames` is supported by every browser admin-v2 targets and by
 * the Node used in tests.
 */
const dutchCountryNames = new Intl.DisplayNames(["nl"], { type: "region" });

function countryLabel(country: CountryCode): string {
  // `of()` returns the input back when it has no name for a region, which is
  // fine as a last-resort label.
  return dutchCountryNames.of(country) ?? country;
}

export interface PhoneCountryOption {
  readonly value: CountryCode;
  /** e.g. "Nederland" */
  readonly label: string;
  /** e.g. "+31" */
  readonly callingCode: string;
}

/**
 * A realistic example number for `country`, in national notation, for use as
 * an input placeholder (NL `06 12345678`, BE `0470 12 34 56`, DE
 * `01512 3456789`).
 *
 * Taken from libphonenumber's own example-number metadata rather than being
 * hardcoded: the national formats differ per country in both digit count and
 * grouping, so a single literal placeholder is wrong everywhere but one.
 */
export function phonePlaceholder(country: CountryCode): string {
  return getExampleNumber(country, examples)?.formatNational() ?? "";
}

/** The selectable countries, in {@link SUPPORTED_COUNTRIES} order. */
export const phoneCountryOptions: readonly PhoneCountryOption[] =
  SUPPORTED_COUNTRIES.map((country) => ({
    value: country,
    label: countryLabel(country),
    callingCode: `+${getCountryCallingCode(country)}`,
  }));

/**
 * Validates a national-notation number against a specific country's rules.
 * Empty input is treated as valid here — "required or not" is the schema's
 * decision, not this helper's.
 */
export function isValidPhoneForCountry(
  value: string,
  country: CountryCode,
): boolean {
  if (value.trim() === "") return true;
  const parsed = parsePhoneNumberFromString(value, country);
  return parsed?.isValid() ?? false;
}

/**
 * Normalises a national-notation number to the E.164 form the backend
 * requires (`PersonInput.telephone` runs libphonenumber and rejects national
 * notation like `06 12 34 56 78`). Returns `""` for empty input, and falls
 * back to the raw trimmed value when it can't be parsed so the backend still
 * has the final say rather than us silently dropping what the user typed.
 */
export function toE164(value: string, country: CountryCode): string {
  if (value.trim() === "") return "";
  const parsed = parsePhoneNumberFromString(value, country);
  return parsed ? parsed.format("E.164") : value.trim();
}

/**
 * Splits a stored E.164 number back into a country + national-notation pair,
 * so an existing value can be loaded into the split control. Falls back to
 * {@link DEFAULT_PHONE_COUNTRY} and the raw value when unparseable.
 */
export function fromE164(value: string): {
  country: CountryCode;
  national: string;
} {
  const parsed = value ? parsePhoneNumberFromString(value) : undefined;
  if (!parsed?.country) {
    return { country: DEFAULT_PHONE_COUNTRY, national: value };
  }
  return {
    country: parsed.country,
    national: parsed.formatNational(),
  };
}
